'use client';

import { useEffect, useState } from 'react';
import { getAccessToken, refreshMe } from '@/lib/auth';
import { getApiUrl } from '@/lib/settings';
import { Button } from '@/components/Button';
import { HeroCard } from '@/components/HeroCard';

/**
 * Full-screen, blocking acceptance of the app-wide Terms of Use.
 *
 * Rendered by `NeedsAuth` whenever the authenticated user's
 * `me.app_terms_required` is true — the server's AppTermsGuardMiddleware
 * 412s every data route until acceptance, so this is the only thing the
 * user can reach. On accept we re-pull `/me`, which flips the flag and
 * lets `NeedsAuth` render the real page.
 *
 * One hero per surface (DESIGN.md §1/§8.2); the terms text lives in a
 * restrained card below the hero.
 */

type AppTerms = {
  version: string;
  hash: string;
  text: string;
  requires_acceptance: boolean;
};

export function AppTermsGate() {
  const [terms, setTerms] = useState<AppTerms | null>(null);
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const token = getAccessToken();
        const resp = await fetch(`${getApiUrl()}/api/terms`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const body = (await resp.json()) as AppTerms;
        if (!cancelled) setTerms(body);
      } catch {
        if (!cancelled) setErr('Could not load the terms. Please retry.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function accept() {
    if (!terms) return;
    setBusy(true);
    setErr(null);
    try {
      const token = getAccessToken();
      const resp = await fetch(`${getApiUrl()}/api/terms/accept`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ version: terms.version, hash: terms.hash }),
      });
      if (resp.status === 409) {
        // The text changed under us — re-fetch and ask again.
        setErr('The terms changed. Please review the updated text and agree.');
        setAgreed(false);
        const fresh = (await (
          await fetch(`${getApiUrl()}/api/terms`, {
            headers: { Authorization: `Bearer ${token}` },
          })
        ).json()) as AppTerms;
        setTerms(fresh);
        return;
      }
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      // Re-read /me so `app_terms_required` flips false and NeedsAuth
      // swaps this gate out for the real page.
      await refreshMe();
    } catch {
      setErr('Could not record your acceptance. Please retry.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto flex min-h-[80vh] max-w-2xl flex-col justify-center gap-6 py-10">
      <HeroCard>
        <p className="text-xs uppercase tracking-[0.06em] text-fg-inverse/80">
          Before you start
        </p>
        <h1
          className="mt-1 text-3xl leading-tight"
          style={{ fontFamily: 'var(--font-display)' }}
        >
          Terms of Use
        </h1>
        <p className="mt-2 max-w-md text-sm text-fg-inverse/90">
          Flowlet reads your connected accounts (novobanco, Revolut, PayPal)
          read-only, stores that data encrypted, and uses AI to categorize
          transactions. Please review and accept to continue.
        </p>
      </HeroCard>

      <div className="flex flex-col gap-4 rounded-md bg-surface p-6 shadow-sm">
        {terms ? (
          <>
            <pre className="max-h-80 overflow-auto whitespace-pre-wrap rounded-sm bg-sunken p-4 font-mono text-xs leading-relaxed text-fg-primary">
              {terms.text}
            </pre>
            <label className="flex cursor-pointer items-start gap-2 text-sm text-fg-primary">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="mt-0.5"
              />
              <span>
                I have read and agree to the Terms of Use (version{' '}
                <code className="font-mono">{terms.version}</code>),
                including the read-only access, encrypted storage, and AI
                categorization described above.
              </span>
            </label>
            {err && <p className="text-xs text-(--color-negative)">{err}</p>}
            <div>
              <Button
                variant="primary"
                onClick={accept}
                disabled={!agreed}
                loading={busy}
              >
                Accept and continue
              </Button>
            </div>
          </>
        ) : err ? (
          <p className="text-sm text-(--color-negative)">{err}</p>
        ) : (
          <p className="text-sm text-fg-muted">Loading terms…</p>
        )}
      </div>
    </div>
  );
}
